'use client';

import { useState, useEffect } from 'react';
import { useTranslations } from 'next-intl';

export default function AreaCalculator() {
  const t = useTranslations("tools.areaCalculator");
  const [screenSize, setScreenSize] = useState('mobile');

  // Shape states
  const [rectangleInputs, setRectangleInputs] = useState({
    length: 30,
    width: 20,
    lengthUnit: 'feet',
    widthUnit: 'feet'
  });
  const [rectangleResult, setRectangleResult] = useState<string | null>(null);

  const [circleInputs, setCircleInputs] = useState({
    radius: 15,
    unit: 'feet'
  });
  const [circleResult, setCircleResult] = useState<string | null>(null);

  const [triangleInputs, setTriangleInputs] = useState({
    sideA: 30,
    sideB: 45,
    sideC: 50,
    unitA: 'feet',
    unitB: 'feet',
    unitC: 'feet'
  });
  const [triangleResult, setTriangleResult] = useState<string | null>(null);

  const [trapezoidInputs, setTrapezoidInputs] = useState({
    base1: 30,
    base2: 45,
    height: 20,
    unit1: 'feet',
    unit2: 'feet',
    unitH: 'feet'
  });
  const [trapezoidResult, setTrapezoidResult] = useState<string | null>(null);

  const [parallelogramInputs, setParallelogramInputs] = useState({
    base: 30,
    height: 20,
    unitB: 'feet',
    unitH: 'feet'
  });
  const [parallelogramResult, setParallelogramResult] = useState<string | null>(null);

  const [ellipseInputs, setEllipseInputs] = useState({
    major: 30,
    minor: 20,
    unitMajor: 'feet',
    unitMinor: 'feet'
  });
  const [ellipseResult, setEllipseResult] = useState<string | null>(null);

  useEffect(() => {
    const updateScreenSize = () => {
      const width = window.innerWidth;
      if (width >= 1024) {
        setScreenSize('desktop');
      } else if (width >= 768) {
        setScreenSize('tablet');
      } else {
        setScreenSize('mobile');
      }
    };

    updateScreenSize();
    window.addEventListener('resize', updateScreenSize);
    return () => window.removeEventListener('resize', updateScreenSize);
  }, []);

  const getResponsiveStyle = (baseStyle: any, mobileOverrides?: any, tabletOverrides?: any, desktopOverrides?: any) => {
    let style = { ...baseStyle };

    if (screenSize === 'mobile' && mobileOverrides) {
      style = { ...style, ...mobileOverrides };
    } else if (screenSize === 'tablet' && tabletOverrides) {
      style = { ...style, ...tabletOverrides };
    } else if (screenSize === 'desktop' && desktopOverrides) {
      style = { ...style, ...desktopOverrides };
    }

    return style;
  };

  const colors = {
    primary: '#ff5a70',
    primaryHover: '#f63b3b',
    secondary: '#ffc400',
    accent: '#ff6b95',
    textPrimary: '#2d3748',
    textSecondary: '#4a5568',
    glassBg: 'rgba(255, 255, 255, 0.6)',
    glassBorder: 'rgba(255, 200, 180, 0.2)',
  };

  // Unit conversion utilities
  const unitConversions: { [key: string]: number } = {
    feet: 1,
    meters: 3.28084,
    yards: 0.333333,
    inches: 12,
    centimeters: 30.48
  };

  const convertToFeet = (value: number, unit: string): number => {
    return value / unitConversions[unit];
  };

  const formatArea = (areaInSquareFeet: number, originalUnit: string): string => {
    const conversionFactor = unitConversions[originalUnit];
    const areaInOriginalUnit = areaInSquareFeet * (conversionFactor * conversionFactor);

    let unitLabel = originalUnit === 'centimeters' ? 'cm²' :
                   originalUnit === 'meters' ? 'm²' :
                   originalUnit === 'feet' ? 'ft²' :
                   originalUnit === 'yards' ? 'yd²' : 'in²';

    if (areaInOriginalUnit >= 1000000) {
      return areaInOriginalUnit.toExponential(2) + ' ' + unitLabel;
    } else if (areaInOriginalUnit >= 1000) {
      return areaInOriginalUnit.toLocaleString('en-US', {maximumFractionDigits: 2}) + ' ' + unitLabel;
    } else {
      return areaInOriginalUnit.toFixed(2) + ' ' + unitLabel;
    }
  };

  // Calculation functions
  const calculateRectangle = (e: React.FormEvent) => {
    e.preventDefault();
    const lengthFeet = convertToFeet(rectangleInputs.length, rectangleInputs.lengthUnit);
    const widthFeet = convertToFeet(rectangleInputs.width, rectangleInputs.widthUnit);
    const area = lengthFeet * widthFeet;

    if (isNaN(area) || area <= 0) {
      setRectangleResult(t('invalidInput'));
    } else {
      setRectangleResult(formatArea(area, rectangleInputs.lengthUnit));
    }
  };

  const calculateCircle = (e: React.FormEvent) => {
    e.preventDefault();
    const radiusFeet = convertToFeet(circleInputs.radius, circleInputs.unit);
    const area = Math.PI * radiusFeet * radiusFeet;

    if (isNaN(area) || area <= 0) {
      setCircleResult(t('invalidInput'));
    } else {
      setCircleResult(formatArea(area, circleInputs.unit));
    }
  };

  const calculateTriangle = (e: React.FormEvent) => {
    e.preventDefault();
    const aFeet = convertToFeet(triangleInputs.sideA, triangleInputs.unitA);
    const bFeet = convertToFeet(triangleInputs.sideB, triangleInputs.unitB);
    const cFeet = convertToFeet(triangleInputs.sideC, triangleInputs.unitC);
    const s = (aFeet + bFeet + cFeet) / 2;
    const area = Math.sqrt(s * (s - aFeet) * (s - bFeet) * (s - cFeet));

    if (isNaN(area) || area <= 0) {
      setTriangleResult(t('invalidInput'));
    } else {
      setTriangleResult(formatArea(area, triangleInputs.unitA));
    }
  };

  const calculateTrapezoid = (e: React.FormEvent) => {
    e.preventDefault();
    const b1Feet = convertToFeet(trapezoidInputs.base1, trapezoidInputs.unit1);
    const b2Feet = convertToFeet(trapezoidInputs.base2, trapezoidInputs.unit2);
    const hFeet = convertToFeet(trapezoidInputs.height, trapezoidInputs.unitH);
    const area = 0.5 * (b1Feet + b2Feet) * hFeet;

    if (isNaN(area) || area <= 0) {
      setTrapezoidResult(t('invalidInput'));
    } else {
      setTrapezoidResult(formatArea(area, trapezoidInputs.unit1));
    }
  };

  const calculateParallelogram = (e: React.FormEvent) => {
    e.preventDefault();
    const baseFeet = convertToFeet(parallelogramInputs.base, parallelogramInputs.unitB);
    const heightFeet = convertToFeet(parallelogramInputs.height, parallelogramInputs.unitH);
    const area = baseFeet * heightFeet;

    if (isNaN(area) || area <= 0) {
      setParallelogramResult(t('invalidInput'));
    } else {
      setParallelogramResult(formatArea(area, parallelogramInputs.unitB));
    }
  };

  const calculateEllipse = (e: React.FormEvent) => {
    e.preventDefault();
    const majorFeet = convertToFeet(ellipseInputs.major, ellipseInputs.unitMajor);
    const minorFeet = convertToFeet(ellipseInputs.minor, ellipseInputs.unitMinor);
    const area = Math.PI * majorFeet * minorFeet;

    if (isNaN(area) || area <= 0) {
      setEllipseResult(t('invalidInput'));
    } else {
      setEllipseResult(formatArea(area, ellipseInputs.unitMajor));
    }
  };

  const containerStyle = getResponsiveStyle(
    { maxWidth: '1200px', margin: '0 auto', position: 'relative' as const, zIndex: 1 },
    { padding: '1rem' },
    { padding: '1.5rem' },
    { padding: '2rem' }
  );

  const headerStyle = getResponsiveStyle(
    { textAlign: 'center' as const, marginBottom: '2rem' },
    { marginBottom: '2rem' },
    { marginBottom: '2.5rem' },
    { marginBottom: '3rem' }
  );

  const h1Style = getResponsiveStyle(
    { fontWeight: 700, color: colors.textPrimary, marginBottom: '1rem', lineHeight: 1.2 },
    { fontSize: '2rem' },
    { fontSize: '2.5rem' },
    { fontSize: '3rem' }
  );

  const summaryTextStyle = getResponsiveStyle(
    { color: colors.textSecondary, marginBottom: '0' },
    { fontSize: '1rem' },
    { fontSize: '1.125rem' },
    { fontSize: '1.25rem' }
  );

  const calculatorToolStyle = getResponsiveStyle(
    {
      background: 'linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%)',
      borderRadius: '1rem',
      marginBottom: '2rem'
    },
    { padding: '1.5rem 1rem' },
    { padding: '2rem 1.5rem', borderRadius: '1.25rem' },
    { padding: '3rem 1.5rem', borderRadius: '1.5rem' }
  );

  const toolHeaderStyle = getResponsiveStyle(
    { textAlign: 'center' as const, marginBottom: '2rem' },
    { marginBottom: '1.5rem' },
    { marginBottom: '2rem' },
    { marginBottom: '2.5rem' }
  );

  const h2Style = getResponsiveStyle(
    { fontWeight: 600, color: colors.textPrimary, marginBottom: '1rem' },
    { fontSize: '1.5rem' },
    { fontSize: '1.75rem' },
    { fontSize: '2rem' }
  );

  const shapesGridStyle = getResponsiveStyle(
    {
      display: 'grid',
      gap: '1.5rem',
      marginBottom: '2rem'
    },
    { gridTemplateColumns: '1fr' },
    { gridTemplateColumns: 'repeat(2, 1fr)' },
    { gridTemplateColumns: 'repeat(3, 1fr)' }
  );

  const shapeCardStyle: React.CSSProperties = {
    background: 'white',
    borderRadius: '1rem',
    padding: '1.5rem',
    boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
    transition: 'transform 0.2s, box-shadow 0.2s'
  };

  const shapeHeaderStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: '1.25rem',
    paddingBottom: '1rem',
    borderBottom: '2px solid #e2e8f0'
  };

  const shapeTitleStyle = getResponsiveStyle(
    { fontWeight: 600, color: colors.textPrimary, display: 'flex', alignItems: 'center', gap: '0.5rem' },
    { fontSize: '1.125rem' },
    { fontSize: '1.25rem' },
    { fontSize: '1.25rem' }
  );

  const shapeIconStyle: React.CSSProperties = {
    width: '60px',
    height: '60px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: 'linear-gradient(135deg, #2563eb 0%, #7c3aed 100%)',
    borderRadius: '0.5rem',
    color: 'white',
    fontSize: '1.5rem'
  };

  const formGroupStyle: React.CSSProperties = {
    marginBottom: '1rem'
  };

  const labelStyle: React.CSSProperties = {
    display: 'block',
    marginBottom: '0.5rem',
    color: '#475569',
    fontWeight: 500,
    fontSize: '0.875rem'
  };

  const inputWrapperStyle: React.CSSProperties = {
    display: 'flex',
    gap: '0.5rem'
  };

  const inputStyle: React.CSSProperties = {
    flex: 1,
    padding: '0.625rem 0.875rem',
    border: '2px solid #e2e8f0',
    borderRadius: '0.5rem',
    fontSize: '0.95rem',
    transition: 'border-color 0.2s, box-shadow 0.2s'
  };

  const selectStyle: React.CSSProperties = {
    padding: '0.625rem 0.875rem',
    border: '2px solid #e2e8f0',
    borderRadius: '0.5rem',
    fontSize: '0.95rem',
    background: 'white',
    cursor: 'pointer',
    transition: 'border-color 0.2s'
  };

  const buttonGroupStyle = getResponsiveStyle(
    { display: 'flex', gap: '0.75rem', marginTop: '1.25rem' },
    { flexDirection: 'column' as const },
    { flexDirection: 'row' as const },
    { flexDirection: 'row' as const }
  );

  const calculateBtnStyle: React.CSSProperties = {
    flex: 1,
    padding: '0.75rem 1.5rem',
    border: 'none',
    borderRadius: '0.5rem',
    fontSize: '0.95rem',
    fontWeight: 600,
    cursor: 'pointer',
    transition: 'all 0.2s',
    background: 'linear-gradient(135deg, #2563eb 0%, #7c3aed 100%)',
    color: 'white'
  };

  const clearBtnStyle: React.CSSProperties = {
    flex: 1,
    padding: '0.75rem 1.5rem',
    border: 'none',
    borderRadius: '0.5rem',
    fontSize: '0.95rem',
    fontWeight: 600,
    cursor: 'pointer',
    transition: 'all 0.2s',
    background: '#f1f5f9',
    color: '#475569'
  };

  const resultBoxStyle: React.CSSProperties = {
    marginTop: '1.25rem',
    padding: '1rem',
    background: 'linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%)',
    borderRadius: '0.75rem',
    borderLeft: '4px solid #22c55e'
  };

  const resultLabelStyle: React.CSSProperties = {
    fontSize: '0.875rem',
    color: '#166534',
    fontWeight: 500,
    display: 'block',
    marginBottom: '0.25rem'
  };

  const resultValueStyle: React.CSSProperties = {
    fontSize: '1.5rem',
    color: '#15803d',
    fontWeight: 700
  };

  const formulaHintStyle: React.CSSProperties = {
    marginTop: '0.75rem',
    padding: '0.75rem',
    background: '#fef3c7',
    borderRadius: '0.5rem',
    fontSize: '0.875rem',
    color: '#92400e',
    borderLeft: '3px solid #f59e0b'
  };

  const glassCardStyle = getResponsiveStyle(
    {
      background: colors.glassBg,
      backdropFilter: 'blur(20px) saturate(180%)',
      WebkitBackdropFilter: 'blur(20px) saturate(180%)',
      border: `1px solid ${colors.glassBorder}`,
      borderRadius: '1rem',
      boxShadow: '0 8px 32px 0 rgba(255, 90, 112, 0.08)',
      marginBottom: '2rem'
    },
    { padding: '1.5rem' },
    { padding: '2rem', borderRadius: '1.25rem' },
    { padding: '2.5rem', borderRadius: '1.5rem' }
  );

  const paragraphStyle = getResponsiveStyle(
    { lineHeight: 1.6, color: colors.textSecondary, marginBottom: '1rem' },
    { fontSize: '1rem' },
    { fontSize: '1.125rem', lineHeight: 1.7 },
    { fontSize: '1.25rem', lineHeight: 1.8 }
  );

  const listStyle = getResponsiveStyle(
    { lineHeight: 1.6, color: colors.textSecondary, paddingLeft: '1.5rem', marginBottom: '1rem' },
    { fontSize: '1rem' },
    { fontSize: '1.125rem', lineHeight: 1.7 },
    { fontSize: '1.25rem', lineHeight: 1.8 }
  );

  const h3Style = getResponsiveStyle(
    { fontWeight: 600, color: colors.textPrimary, marginTop: '1.5rem', marginBottom: '0.75rem' },
    { fontSize: '1.25rem' },
    { fontSize: '1.375rem' },
    { fontSize: '1.5rem' }
  );

  const faqItemStyle = getResponsiveStyle(
    { marginBottom: '1.5rem' },
    { marginBottom: '1.5rem' },
    { marginBottom: '2rem' },
    { marginBottom: '2rem' }
  );

  const refLinkStyle: React.CSSProperties = {
    color: colors.primary,
    textDecoration: 'none',
    transition: 'color 0.2s'
  };

  return (
    <div style={containerStyle}>
      <header style={headerStyle}>
        <h1 style={h1Style}>{t('title')}</h1>
        <p style={summaryTextStyle}>{t('subtitle')}</p>
      </header>

      {/* Calculator Tool */}
      <section style={calculatorToolStyle}>
        <div style={toolHeaderStyle}>
          <h2 style={h2Style}>{t('toolTitle')}</h2>
          <p style={summaryTextStyle}>{t('toolSubtitle')}</p>
        </div>

        <div style={shapesGridStyle}>
          {/* Rectangle */}
          <div style={shapeCardStyle}>
            <div style={shapeHeaderStyle}>
              <h3 style={shapeTitleStyle}>{t('rectangleTitle')}</h3>
              <div style={shapeIconStyle}>▭</div>
            </div>
            <form onSubmit={calculateRectangle}>
              <div style={formGroupStyle}>
                <label style={labelStyle}>{t('rectangleLength')}</label>
                <div style={inputWrapperStyle}>
                  <input
                    type="number"
                    step="any"
                    value={rectangleInputs.length}
                    onChange={(e) => setRectangleInputs({...rectangleInputs, length: parseFloat(e.target.value)})}
                    style={inputStyle}
                    required
                  />
                  <select
                    value={rectangleInputs.lengthUnit}
                    onChange={(e) => setRectangleInputs({...rectangleInputs, lengthUnit: e.target.value})}
                    style={selectStyle}
                  >
                    <option value="feet">{t('unitFeet')}</option>
                    <option value="meters">{t('unitMeters')}</option>
                    <option value="yards">{t('unitYards')}</option>
                    <option value="inches">{t('unitInches')}</option>
                    <option value="centimeters">{t('unitCentimeters')}</option>
                  </select>
                </div>
              </div>
              <div style={formGroupStyle}>
                <label style={labelStyle}>{t('rectangleWidth')}</label>
                <div style={inputWrapperStyle}>
                  <input
                    type="number"
                    step="any"
                    value={rectangleInputs.width}
                    onChange={(e) => setRectangleInputs({...rectangleInputs, width: parseFloat(e.target.value)})}
                    style={inputStyle}
                    required
                  />
                  <select
                    value={rectangleInputs.widthUnit}
                    onChange={(e) => setRectangleInputs({...rectangleInputs, widthUnit: e.target.value})}
                    style={selectStyle}
                  >
                    <option value="feet">{t('unitFeet')}</option>
                    <option value="meters">{t('unitMeters')}</option>
                    <option value="yards">{t('unitYards')}</option>
                    <option value="inches">{t('unitInches')}</option>
                    <option value="centimeters">{t('unitCentimeters')}</option>
                  </select>
                </div>
              </div>
              <div style={buttonGroupStyle}>
                <button type="submit" style={calculateBtnStyle}>{t('calculateButton')}</button>
                <button type="button" style={clearBtnStyle} onClick={() => {
                  setRectangleInputs({ length: 30, width: 20, lengthUnit: 'feet', widthUnit: 'feet' });
                  setRectangleResult(null);
                }}>{t('clearButton')}</button>
              </div>
              {rectangleResult && (
                <div style={resultBoxStyle}>
                  <span style={resultLabelStyle}>{t('resultLabel')}</span>
                  <span style={resultValueStyle}>{rectangleResult}</span>
                  <div style={formulaHintStyle}>{t('rectangleFormula')}</div>
                </div>
              )}
            </form>
          </div>

          {/* Circle */}
          <div style={shapeCardStyle}>
            <div style={shapeHeaderStyle}>
              <h3 style={shapeTitleStyle}>{t('circleTitle')}</h3>
              <div style={shapeIconStyle}>○</div>
            </div>
            <form onSubmit={calculateCircle}>
              <div style={formGroupStyle}>
                <label style={labelStyle}>{t('circleRadius')}</label>
                <div style={inputWrapperStyle}>
                  <input
                    type="number"
                    step="any"
                    value={circleInputs.radius}
                    onChange={(e) => setCircleInputs({...circleInputs, radius: parseFloat(e.target.value)})}
                    style={inputStyle}
                    required
                  />
                  <select
                    value={circleInputs.unit}
                    onChange={(e) => setCircleInputs({...circleInputs, unit: e.target.value})}
                    style={selectStyle}
                  >
                    <option value="feet">{t('unitFeet')}</option>
                    <option value="meters">{t('unitMeters')}</option>
                    <option value="yards">{t('unitYards')}</option>
                    <option value="inches">{t('unitInches')}</option>
                    <option value="centimeters">{t('unitCentimeters')}</option>
                  </select>
                </div>
              </div>
              <div style={buttonGroupStyle}>
                <button type="submit" style={calculateBtnStyle}>{t('calculateButton')}</button>
                <button type="button" style={clearBtnStyle} onClick={() => {
                  setCircleInputs({ radius: 15, unit: 'feet' });
                  setCircleResult(null);
                }}>{t('clearButton')}</button>
              </div>
              {circleResult && (
                <div style={resultBoxStyle}>
                  <span style={resultLabelStyle}>{t('resultLabel')}</span>
                  <span style={resultValueStyle}>{circleResult}</span>
                  <div style={formulaHintStyle}>{t('circleFormula')}</div>
                </div>
              )}
            </form>
          </div>

          {/* Triangle */}
          <div style={shapeCardStyle}>
            <div style={shapeHeaderStyle}>
              <h3 style={shapeTitleStyle}>{t('triangleTitle')}</h3>
              <div style={shapeIconStyle}>△</div>
            </div>
            <form onSubmit={calculateTriangle}>
              <div style={formGroupStyle}>
                <label style={labelStyle}>{t('triangleSideA')}</label>
                <div style={inputWrapperStyle}>
                  <input
                    type="number"
                    step="any"
                    value={triangleInputs.sideA}
                    onChange={(e) => setTriangleInputs({...triangleInputs, sideA: parseFloat(e.target.value)})}
                    style={inputStyle}
                    required
                  />
                  <select
                    value={triangleInputs.unitA}
                    onChange={(e) => setTriangleInputs({...triangleInputs, unitA: e.target.value})}
                    style={selectStyle}
                  >
                    <option value="feet">{t('unitFeet')}</option>
                    <option value="meters">{t('unitMeters')}</option>
                    <option value="yards">{t('unitYards')}</option>
                    <option value="inches">{t('unitInches')}</option>
                    <option value="centimeters">{t('unitCentimeters')}</option>
                  </select>
                </div>
              </div>
              <div style={formGroupStyle}>
                <label style={labelStyle}>{t('triangleSideB')}</label>
                <div style={inputWrapperStyle}>
                  <input
                    type="number"
                    step="any"
                    value={triangleInputs.sideB}
                    onChange={(e) => setTriangleInputs({...triangleInputs, sideB: parseFloat(e.target.value)})}
                    style={inputStyle}
                    required
                  />
                  <select
                    value={triangleInputs.unitB}
                    onChange={(e) => setTriangleInputs({...triangleInputs, unitB: e.target.value})}
                    style={selectStyle}
                  >
                    <option value="feet">{t('unitFeet')}</option>
                    <option value="meters">{t('unitMeters')}</option>
                    <option value="yards">{t('unitYards')}</option>
                    <option value="inches">{t('unitInches')}</option>
                    <option value="centimeters">{t('unitCentimeters')}</option>
                  </select>
                </div>
              </div>
              <div style={formGroupStyle}>
                <label style={labelStyle}>{t('triangleSideC')}</label>
                <div style={inputWrapperStyle}>
                  <input
                    type="number"
                    step="any"
                    value={triangleInputs.sideC}
                    onChange={(e) => setTriangleInputs({...triangleInputs, sideC: parseFloat(e.target.value)})}
                    style={inputStyle}
                    required
                  />
                  <select
                    value={triangleInputs.unitC}
                    onChange={(e) => setTriangleInputs({...triangleInputs, unitC: e.target.value})}
                    style={selectStyle}
                  >
                    <option value="feet">{t('unitFeet')}</option>
                    <option value="meters">{t('unitMeters')}</option>
                    <option value="yards">{t('unitYards')}</option>
                    <option value="inches">{t('unitInches')}</option>
                    <option value="centimeters">{t('unitCentimeters')}</option>
                  </select>
                </div>
              </div>
              <div style={buttonGroupStyle}>
                <button type="submit" style={calculateBtnStyle}>{t('calculateButton')}</button>
                <button type="button" style={clearBtnStyle} onClick={() => {
                  setTriangleInputs({ sideA: 30, sideB: 45, sideC: 50, unitA: 'feet', unitB: 'feet', unitC: 'feet' });
                  setTriangleResult(null);
                }}>{t('clearButton')}</button>
              </div>
              {triangleResult && (
                <div style={resultBoxStyle}>
                  <span style={resultLabelStyle}>{t('resultLabel')}</span>
                  <span style={resultValueStyle}>{triangleResult}</span>
                  <div style={formulaHintStyle}>{t('triangleFormula')}</div>
                </div>
              )}
            </form>
          </div>

          {/* Trapezoid */}
          <div style={shapeCardStyle}>
            <div style={shapeHeaderStyle}>
              <h3 style={shapeTitleStyle}>{t('trapezoidTitle')}</h3>
              <div style={shapeIconStyle}>⏢</div>
            </div>
            <form onSubmit={calculateTrapezoid}>
              <div style={formGroupStyle}>
                <label style={labelStyle}>{t('trapezoidBase1')}</label>
                <div style={inputWrapperStyle}>
                  <input
                    type="number"
                    step="any"
                    value={trapezoidInputs.base1}
                    onChange={(e) => setTrapezoidInputs({...trapezoidInputs, base1: parseFloat(e.target.value)})}
                    style={inputStyle}
                    required
                  />
                  <select
                    value={trapezoidInputs.unit1}
                    onChange={(e) => setTrapezoidInputs({...trapezoidInputs, unit1: e.target.value})}
                    style={selectStyle}
                  >
                    <option value="feet">{t('unitFeet')}</option>
                    <option value="meters">{t('unitMeters')}</option>
                    <option value="yards">{t('unitYards')}</option>
                    <option value="inches">{t('unitInches')}</option>
                    <option value="centimeters">{t('unitCentimeters')}</option>
                  </select>
                </div>
              </div>
              <div style={formGroupStyle}>
                <label style={labelStyle}>{t('trapezoidBase2')}</label>
                <div style={inputWrapperStyle}>
                  <input
                    type="number"
                    step="any"
                    value={trapezoidInputs.base2}
                    onChange={(e) => setTrapezoidInputs({...trapezoidInputs, base2: parseFloat(e.target.value)})}
                    style={inputStyle}
                    required
                  />
                  <select
                    value={trapezoidInputs.unit2}
                    onChange={(e) => setTrapezoidInputs({...trapezoidInputs, unit2: e.target.value})}
                    style={selectStyle}
                  >
                    <option value="feet">{t('unitFeet')}</option>
                    <option value="meters">{t('unitMeters')}</option>
                    <option value="yards">{t('unitYards')}</option>
                    <option value="inches">{t('unitInches')}</option>
                    <option value="centimeters">{t('unitCentimeters')}</option>
                  </select>
                </div>
              </div>
              <div style={formGroupStyle}>
                <label style={labelStyle}>{t('trapezoidHeight')}</label>
                <div style={inputWrapperStyle}>
                  <input
                    type="number"
                    step="any"
                    value={trapezoidInputs.height}
                    onChange={(e) => setTrapezoidInputs({...trapezoidInputs, height: parseFloat(e.target.value)})}
                    style={inputStyle}
                    required
                  />
                  <select
                    value={trapezoidInputs.unitH}
                    onChange={(e) => setTrapezoidInputs({...trapezoidInputs, unitH: e.target.value})}
                    style={selectStyle}
                  >
                    <option value="feet">{t('unitFeet')}</option>
                    <option value="meters">{t('unitMeters')}</option>
                    <option value="yards">{t('unitYards')}</option>
                    <option value="inches">{t('unitInches')}</option>
                    <option value="centimeters">{t('unitCentimeters')}</option>
                  </select>
                </div>
              </div>
              <div style={buttonGroupStyle}>
                <button type="submit" style={calculateBtnStyle}>{t('calculateButton')}</button>
                <button type="button" style={clearBtnStyle} onClick={() => {
                  setTrapezoidInputs({ base1: 30, base2: 45, height: 20, unit1: 'feet', unit2: 'feet', unitH: 'feet' });
                  setTrapezoidResult(null);
                }}>{t('clearButton')}</button>
              </div>
              {trapezoidResult && (
                <div style={resultBoxStyle}>
                  <span style={resultLabelStyle}>{t('resultLabel')}</span>
                  <span style={resultValueStyle}>{trapezoidResult}</span>
                  <div style={formulaHintStyle}>{t('trapezoidFormula')}</div>
                </div>
              )}
            </form>
          </div>

          {/* Parallelogram */}
          <div style={shapeCardStyle}>
            <div style={shapeHeaderStyle}>
              <h3 style={shapeTitleStyle}>{t('parallelogramTitle')}</h3>
              <div style={shapeIconStyle}>▱</div>
            </div>
            <form onSubmit={calculateParallelogram}>
              <div style={formGroupStyle}>
                <label style={labelStyle}>{t('parallelogramBase')}</label>
                <div style={inputWrapperStyle}>
                  <input
                    type="number"
                    step="any"
                    value={parallelogramInputs.base}
                    onChange={(e) => setParallelogramInputs({...parallelogramInputs, base: parseFloat(e.target.value)})}
                    style={inputStyle}
                    required
                  />
                  <select
                    value={parallelogramInputs.unitB}
                    onChange={(e) => setParallelogramInputs({...parallelogramInputs, unitB: e.target.value})}
                    style={selectStyle}
                  >
                    <option value="feet">{t('unitFeet')}</option>
                    <option value="meters">{t('unitMeters')}</option>
                    <option value="yards">{t('unitYards')}</option>
                    <option value="inches">{t('unitInches')}</option>
                    <option value="centimeters">{t('unitCentimeters')}</option>
                  </select>
                </div>
              </div>
              <div style={formGroupStyle}>
                <label style={labelStyle}>{t('parallelogramHeight')}</label>
                <div style={inputWrapperStyle}>
                  <input
                    type="number"
                    step="any"
                    value={parallelogramInputs.height}
                    onChange={(e) => setParallelogramInputs({...parallelogramInputs, height: parseFloat(e.target.value)})}
                    style={inputStyle}
                    required
                  />
                  <select
                    value={parallelogramInputs.unitH}
                    onChange={(e) => setParallelogramInputs({...parallelogramInputs, unitH: e.target.value})}
                    style={selectStyle}
                  >
                    <option value="feet">{t('unitFeet')}</option>
                    <option value="meters">{t('unitMeters')}</option>
                    <option value="yards">{t('unitYards')}</option>
                    <option value="inches">{t('unitInches')}</option>
                    <option value="centimeters">{t('unitCentimeters')}</option>
                  </select>
                </div>
              </div>
              <div style={buttonGroupStyle}>
                <button type="submit" style={calculateBtnStyle}>{t('calculateButton')}</button>
                <button type="button" style={clearBtnStyle} onClick={() => {
                  setParallelogramInputs({ base: 30, height: 20, unitB: 'feet', unitH: 'feet' });
                  setParallelogramResult(null);
                }}>{t('clearButton')}</button>
              </div>
              {parallelogramResult && (
                <div style={resultBoxStyle}>
                  <span style={resultLabelStyle}>{t('resultLabel')}</span>
                  <span style={resultValueStyle}>{parallelogramResult}</span>
                  <div style={formulaHintStyle}>{t('parallelogramFormula')}</div>
                </div>
              )}
            </form>
          </div>

          {/* Ellipse */}
          <div style={shapeCardStyle}>
            <div style={shapeHeaderStyle}>
              <h3 style={shapeTitleStyle}>{t('ellipseTitle')}</h3>
              <div style={shapeIconStyle}>⬭</div>
            </div>
            <form onSubmit={calculateEllipse}>
              <div style={formGroupStyle}>
                <label style={labelStyle}>{t('ellipseMajor')}</label>
                <div style={inputWrapperStyle}>
                  <input
                    type="number"
                    step="any"
                    value={ellipseInputs.major}
                    onChange={(e) => setEllipseInputs({...ellipseInputs, major: parseFloat(e.target.value)})}
                    style={inputStyle}
                    required
                  />
                  <select
                    value={ellipseInputs.unitMajor}
                    onChange={(e) => setEllipseInputs({...ellipseInputs, unitMajor: e.target.value})}
                    style={selectStyle}
                  >
                    <option value="feet">{t('unitFeet')}</option>
                    <option value="meters">{t('unitMeters')}</option>
                    <option value="yards">{t('unitYards')}</option>
                    <option value="inches">{t('unitInches')}</option>
                    <option value="centimeters">{t('unitCentimeters')}</option>
                  </select>
                </div>
              </div>
              <div style={formGroupStyle}>
                <label style={labelStyle}>{t('ellipseMinor')}</label>
                <div style={inputWrapperStyle}>
                  <input
                    type="number"
                    step="any"
                    value={ellipseInputs.minor}
                    onChange={(e) => setEllipseInputs({...ellipseInputs, minor: parseFloat(e.target.value)})}
                    style={inputStyle}
                    required
                  />
                  <select
                    value={ellipseInputs.unitMinor}
                    onChange={(e) => setEllipseInputs({...ellipseInputs, unitMinor: e.target.value})}
                    style={selectStyle}
                  >
                    <option value="feet">{t('unitFeet')}</option>
                    <option value="meters">{t('unitMeters')}</option>
                    <option value="yards">{t('unitYards')}</option>
                    <option value="inches">{t('unitInches')}</option>
                    <option value="centimeters">{t('unitCentimeters')}</option>
                  </select>
                </div>
              </div>
              <div style={buttonGroupStyle}>
                <button type="submit" style={calculateBtnStyle}>{t('calculateButton')}</button>
                <button type="button" style={clearBtnStyle} onClick={() => {
                  setEllipseInputs({ major: 30, minor: 20, unitMajor: 'feet', unitMinor: 'feet' });
                  setEllipseResult(null);
                }}>{t('clearButton')}</button>
              </div>
              {ellipseResult && (
                <div style={resultBoxStyle}>
                  <span style={resultLabelStyle}>{t('resultLabel')}</span>
                  <span style={resultValueStyle}>{ellipseResult}</span>
                  <div style={formulaHintStyle}>{t('ellipseFormula')}</div>
                </div>
              )}
            </form>
          </div>
        </div>
      </section>

      {/* Intro Section */}
      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('introTitle')}</h2>
        <p style={paragraphStyle}>{t('introParagraph1')}</p>
        <p style={paragraphStyle}>{t('introParagraph2')}</p>
        <p style={paragraphStyle}>{t('introParagraph3')}</p>
      </section>

      {/* How to Use */}
      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('howToUseTitle')}</h2>
        <ol style={listStyle}>
          <li style={{marginBottom: '0.75rem'}}>{t('howToUseStep1')}</li>
          <li style={{marginBottom: '0.75rem'}}>{t('howToUseStep2')}</li>
          <li style={{marginBottom: '0.75rem'}}>{t('howToUseStep3')}</li>
          <li style={{marginBottom: '0.75rem'}}>{t('howToUseStep4')}</li>
          <li style={{marginBottom: '0.75rem'}}>{t('howToUseStep5')}</li>
        </ol>
      </section>

      {/* Insights */}
      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('insightsTitle')}</h2>

        <h3 style={h3Style}>{t('insightsSubtitle1')}</h3>
        <p style={paragraphStyle}>{t('insightsParagraph1')}</p>

        <h3 style={h3Style}>{t('insightsSubtitle2')}</h3>
        <p style={paragraphStyle}>{t('insightsParagraph2')}</p>
        <ul style={listStyle}>
          <li style={{marginBottom: '0.5rem'}}>{t('insightsBullet1')}</li>
          <li style={{marginBottom: '0.5rem'}}>{t('insightsBullet2')}</li>
          <li style={{marginBottom: '0.5rem'}}>{t('insightsBullet3')}</li>
          <li style={{marginBottom: '0.5rem'}}>{t('insightsBullet4')}</li>
        </ul>

        <h3 style={h3Style}>{t('insightsSubtitle3')}</h3>
        <p style={paragraphStyle}>{t('insightsParagraph3')}</p>
        <ul style={listStyle}>
          <li style={{marginBottom: '0.5rem'}}>{t('insightsBullet5')}</li>
          <li style={{marginBottom: '0.5rem'}}>{t('insightsBullet6')}</li>
          <li style={{marginBottom: '0.5rem'}}>{t('insightsBullet7')}</li>
          <li style={{marginBottom: '0.5rem'}}>{t('insightsBullet8')}</li>
        </ul>
      </section>

      {/* Details */}
      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('detailsTitle')}</h2>

        <h3 style={h3Style}>{t('detailsSubtitle1')}</h3>
        <p style={paragraphStyle}>{t('detailsParagraph1')}</p>
        <ul style={listStyle}>
          <li style={{marginBottom: '0.5rem'}}><strong>{t('detailsRectangle')}</strong> {t('detailsRectangleFormula')}</li>
          <li style={{marginBottom: '0.5rem'}}><strong>{t('detailsCircle')}</strong> {t('detailsCircleFormula')}</li>
          <li style={{marginBottom: '0.5rem'}}><strong>{t('detailsTriangle')}</strong> {t('detailsTriangleFormula')}</li>
          <li style={{marginBottom: '0.5rem'}}><strong>{t('detailsTrapezoid')}</strong> {t('detailsTrapezoidFormula')}</li>
          <li style={{marginBottom: '0.5rem'}}><strong>{t('detailsParallelogram')}</strong> {t('detailsParallelogramFormula')}</li>
          <li style={{marginBottom: '0.5rem'}}><strong>{t('detailsEllipse')}</strong> {t('detailsEllipseFormula')}</li>
        </ul>

        <h3 style={h3Style}>{t('detailsSubtitle2')}</h3>
        <p style={paragraphStyle}>{t('detailsParagraph2')}</p>

        <h3 style={h3Style}>{t('detailsSubtitle3')}</h3>
        <p style={paragraphStyle}>{t('detailsParagraph3')}</p>
        <ul style={listStyle}>
          <li style={{marginBottom: '0.5rem'}}>{t('detailsUseCaseFlooring')}</li>
          <li style={{marginBottom: '0.5rem'}}>{t('detailsUseCasePainting')}</li>
          <li style={{marginBottom: '0.5rem'}}>{t('detailsUseCaseLandscaping')}</li>
          <li style={{marginBottom: '0.5rem'}}>{t('detailsUseCaseRealEstate')}</li>
          <li style={{marginBottom: '0.5rem'}}>{t('detailsUseCaseConstruction')}</li>
        </ul>
      </section>

      {/* FAQ */}
      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('faqTitle')}</h2>

        <div style={faqItemStyle}>
          <h3 style={h3Style}>{t('faqQuestion1')}</h3>
          <p style={paragraphStyle}>{t('faqAnswer1')}</p>
        </div>

        <div style={faqItemStyle}>
          <h3 style={h3Style}>{t('faqQuestion2')}</h3>
          <p style={paragraphStyle}>{t('faqAnswer2')}</p>
        </div>

        <div style={faqItemStyle}>
          <h3 style={h3Style}>{t('faqQuestion3')}</h3>
          <p style={paragraphStyle}>{t('faqAnswer3')}</p>
        </div>

        <div style={faqItemStyle}>
          <h3 style={h3Style}>{t('faqQuestion4')}</h3>
          <p style={paragraphStyle}>{t('faqAnswer4')}</p>
        </div>

        <div style={faqItemStyle}>
          <h3 style={h3Style}>{t('faqQuestion5')}</h3>
          <p style={paragraphStyle}>{t('faqAnswer5')}</p>
        </div>

        <div style={faqItemStyle}>
          <h3 style={h3Style}>{t('faqQuestion6')}</h3>
          <p style={paragraphStyle}>{t('faqAnswer6')}</p>
        </div>
      </section>

      {/* References */}
      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('referencesTitle')}</h2>
        <ol style={listStyle}>
          <li style={{marginBottom: '0.5rem'}}>
            <a href="https://mapulator.app/area-calculator-to-measure-irregular-shapes-of-land/" target="_blank" rel="noopener noreferrer" style={refLinkStyle}>
              {t('reference1')}
            </a>
          </li>
          <li style={{marginBottom: '0.5rem'}}>
            <a href="https://lawncrack.com/best-area-calculator/" target="_blank" rel="noopener noreferrer" style={refLinkStyle}>
              {t('reference2')}
            </a>
          </li>
          <li style={{marginBottom: '0.5rem'}}>
            <a href="https://www.casagrand.co.in/blog/top-7-ways-a-land-area-calculator-simplifies-property-deals/" target="_blank" rel="noopener noreferrer" style={refLinkStyle}>
              {t('reference3')}
            </a>
          </li>
          <li style={{marginBottom: '0.5rem'}}>
            <a href="https://www.sketchandcalc.com/blog/need-an-area-calculator/" target="_blank" rel="noopener noreferrer" style={refLinkStyle}>
              {t('reference4')}
            </a>
          </li>
        </ol>
      </section>
    </div>
  );
}